import { useState, CSSProperties } from 'react';
import { CurrentBrand } from 'modules/Types';
import { CampaignCreationSource, trackNewCampaign } from 'pages/MarketingPage/Components/CampaignsSection/analytics';
import BrandSelectionModal from './CreateCampaignModal/SelectBrandModal';
import useCurrentBrand from 'Hooks/useCurrentBrand';
import DefaultAdditionalActions from './DefaultAdditionalActions';
import useScreen from 'Hooks/useScreen';
import { clsx, objectToArray, styx } from "modules/Utils";
import useWebsiteBuilder from 'Hooks/useWebsiteBuilder';
import { PlaybookTypeInfo, PLAYBOOK_TYPE_INFO, WebsiteType } from 'modules/Const';

export const POPULAR_PLAYBOOKS = objectToArray(PLAYBOOK_TYPE_INFO)
    .filter((o: PlaybookTypeInfo) => {
        return [
            "increase_record_streams",
            "pre_save",
            'increase_video_views',
            'grow_instagram_engagement'
        ].indexOf(o.playbookType) !== -1
    })
    .map((playbook: PlaybookTypeInfo) => playbook)

type AskMaestroSource = "Dashboard" | "Marketing" | "Marketing - Team Plan";

export interface QuickActionItem {
    key: string;
    title: string;
    icon: string;
    link?: string;
    isWebsite?: boolean;
}

const defaultQuickActionItems: QuickActionItem[] = [
    {
        key: "music",
        title: "Build a Music Site",
        icon: 'musicSite',
        link: undefined,
        isWebsite: true
    },
    ...POPULAR_PLAYBOOKS.map(campaignType => ({
        key: campaignType.playbookType,
        title: campaignType.title,
        icon: campaignType.icon,
        link: `/marketing/new/${campaignType.playbookType}`,
        isWebsite: false
    }))
];

interface QuickActionsProps {
    className: string;
    style?: CSSProperties;
    showMaestro?: boolean;
    campaignCreationSource?: CampaignCreationSource;
    askMaestroSource?: AskMaestroSource;
    campaignItems?: QuickActionItem[];
    initialSiteType?: WebsiteType;
    allowBrandSelector?: boolean;
    loadedBrands?: CurrentBrand[];
    handleQuickActionClick?: (itemKey: string) => void;
}

const QuickActions = ({
    className,
    showMaestro = true,
    campaignCreationSource = 'dashboard-quick-actions',
    askMaestroSource = 'Dashboard',
    campaignItems = undefined,
    style,
    allowBrandSelector = false,
    loadedBrands = [],
    handleQuickActionClick = (itemKey: string) => { }
}: QuickActionsProps) => {
    const {
        mStyle,
        mobileView,
        mediumView
    } = useScreen();

    const {
        currentBrand,
        setCurrentBrand,
        loadedBrandsDict
    } = useCurrentBrand();

    const {
        setInitialWebsiteType,
        setShowCreateNewWebsiteModal
    } = useWebsiteBuilder();

    const [showBrandSelector, setShowBrandSelector] = useState(false);
    const [inProgressQuickAction, setInProgressQuickAction] = useState<string | undefined>(undefined);

    let actionsToShow = campaignItems || defaultQuickActionItems;

    const handleBrandSelection = async (brandId: number) => {
        setCurrentBrand(loadedBrandsDict![brandId]);

        // Find the quick action item that's in progress
        const quickActionItem = actionsToShow.find(item => item.key === inProgressQuickAction);

        if (quickActionItem?.isWebsite) {
            openCreateSiteModal(inProgressQuickAction);
        } else {
            handleQuickActionClick(inProgressQuickAction!);
        }

        setShowBrandSelector(false);
    }

    const openCreateSiteModal = (websiteKey?: string) => {
        setInitialWebsiteType(websiteKey as WebsiteType || null);
        setShowCreateNewWebsiteModal(true);
    }

    const handleCampaignItemClick = (itemKey: string) => {
        setInProgressQuickAction(itemKey);
        if (allowBrandSelector && loadedBrands?.length) {
            setShowBrandSelector(true);
            return;
        } else {
            defaultCampaignItemClick(itemKey);
        }
    }

    const defaultCampaignItemClick = (itemKey: string) => {
        // Find the campaign item that matches the itemKey

        const item = actionsToShow.find(i => i.key === itemKey);
        if (item?.isWebsite) {
            // Handle website creation tracking
            openCreateSiteModal(itemKey)
        } else {
            // Handle regular campaign tracking
            trackNewCampaign({
                type: itemKey,
                from: 'dashboard-quick-actions',
                zeroState: false,
                aiQuestion: null,
                currentBrand,
                key: itemKey
            });

            if (handleQuickActionClick) {
                handleQuickActionClick(itemKey);
            }
        }
    }

    return (
        <>
            <div
                className={clsx('quickActionsComponent', className)}
                style={styx(
                    mStyle(sty, 'quickActionsComponent'),
                    style
                )}
            >
                <DefaultAdditionalActions
                    className="quickActionsDefaultAdditionalActions"
                    isOnlyActionsView
                    hidePrimaryButton
                    onButtonClick={console.log}
                    campaignItems={actionsToShow}
                    stackedView={mobileView}
                    additionalItemsText={mobileView ? undefined : "Shortcuts:"}
                    onCampaignItemClick={handleCampaignItemClick}
                    style={{ paddingTop: 16, paddingBottom: 16 }}
                />
            </div>

            <BrandSelectionModal
                className="quickActionsBrandSelectionModal"
                isOpen={showBrandSelector}
                onClose={() => setShowBrandSelector(false)}
                onBrandSelect={handleBrandSelection}
                titleText="Select a Profile"
                subtitleText="Choose the profile you want to create a website for."
            />
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    quickActionsComponent: {
        display: 'flex',
        alignSelf: 'flex-start',
        borderRadius: 8,
        backgroundColor: 'white',
        width: '100%',
        overflowX: 'scroll',
    },
    quickActionsComponent_mobile: {
        flexDirection: 'column',
        borderRadius: 0
    },
    maestroCampaignRecommenderQuickActionsContainer: {
        paddingTop: 16,
        paddingLeft: 16,
        paddingRight: 16,
    }
}

export default QuickActions;